<script lang="ts">
  import { onMount } from "svelte";
  import type { SliderItem } from "./type";
  import Icon from "../common/icons/Icon.svelte";
  import checkMobileDevice from "../common/is-mobile";
  import Item from "./SliderItem.svelte";
  import {
    getNextActiveIndex,
    executeAfterTransition,
    reflow,
    setWaitlistSubmitted,
  } from "./utils";

  export let items: SliderItem[];
  export let pauseOnHover: boolean = true;
  export let autoSlide: boolean = true;

  // init waitlist submitted state
  setWaitlistSubmitted(false);
  const TOUCHEVENT_COMPAT_WAIT = 500; // Time for mouse compat events to fire after touch
  const touchSupported =
    "ontouchstart" in document.documentElement || navigator.maxTouchPoints > 0;
  const isMobile = checkMobileDevice();
  const arrowIconWidth = isMobile ? "11px" : "16px";
  const arrowIconHeight = isMobile ? "9px" : "13px";
  let sliderContainerElement: HTMLElement;
  let sliderContentElement: HTMLElement;
  let bottomButtonElement: HTMLElement;
  let arrowButtonsElement: HTMLElement;
  let arrowPosition: string = "-24px";
  let slideIntervalDuration: number = 6500;
  let slideInterval = null;
  let touchEndTimeout = null;
  let activeIndex: number = 0;
  let playIconInitAnimation: boolean = true;
  let playTitleInitAnimation: boolean = true;
  let playDescriptionInitAnimation: boolean = true;
  let playImageInitAnimation: boolean = true;
  let playWaitlistInitAnimation: boolean = true;
  let isSliding: boolean = false;
  let images: NodeListOf<HTMLElement>;
  const SLIDE_ORDER_NEXT = "next";
  const SLIDE_ORDER_PREV = "prev";
  const initAnimationDuration = "1s";
  const switchSlideAnimationDuration = "0.5s";
  // Not sliding when this flag is up
  let stopSliding: boolean = false;

  function handleLeftArrowClick() {
    slide(SLIDE_ORDER_PREV);
  }

  function handleRightArrowClick() {
    slide(SLIDE_ORDER_NEXT);
  }

  function slide(
    order: typeof SLIDE_ORDER_NEXT | typeof SLIDE_ORDER_PREV,
    nextIndex?: number
  ) {
    if (isSliding || !sliderContentElement || stopSliding) {
      return;
    }

    isSliding = true;
    pause();

    const shouldGetNext = order === SLIDE_ORDER_NEXT;
    const nextActiveIndex =
      nextIndex ?? getNextActiveIndex(items, activeIndex, shouldGetNext);
    const direction = order === SLIDE_ORDER_NEXT ? "left" : "right";
    const activeElement: HTMLElement = sliderContentElement.querySelector(
      ".slider-item-container.active"
    );
    const nextElement: HTMLElement = sliderContentElement.querySelectorAll(
      ".slider-item-container"
    )[nextActiveIndex] as HTMLElement;
    const bottomButtonActiveElement: HTMLElement =
      bottomButtonElement.querySelector(".slider-control-button.active");
    const bottomButtonNextElement: HTMLElement =
      bottomButtonElement.querySelectorAll(".slider-control-button")[
        nextActiveIndex
      ] as HTMLElement;

    nextElement.classList.add(order);
    reflow(nextElement);

    activeElement.classList.add(direction);
    nextElement.classList.add(direction);
    bottomButtonActiveElement.classList.add("current");
    bottomButtonNextElement.classList.add("next");

    const completeCallBack = () => {
      activeElement.classList.remove(direction);
      nextElement.classList.remove(direction, order);
      bottomButtonActiveElement.classList.remove("current");
      bottomButtonNextElement.classList.remove("next");
      activeIndex = nextActiveIndex;
      isSliding = false;
    };

    executeAfterTransition(completeCallBack, activeElement, true);
    cycle();
  }

  function slideTo(index: number) {
    if (index > items.length - 1 || index < 0) {
      return;
    }

    if (isSliding) {
      return;
    }

    if (activeIndex === index) {
      pause();
      cycle();
      return;
    }

    const slideOrder =
      index > activeIndex ? SLIDE_ORDER_NEXT : SLIDE_ORDER_PREV;

    slide(slideOrder, index);
  }

  function _clearInterval() {
    if (slideInterval) {
      clearInterval(slideInterval);
      slideInterval = null;
    }
  }

  function pause() {
    // console.log("pause =========== ");
    _clearInterval();
  }

  function cycle() {
    // console.log("cycle ============== ");
    if (!autoSlide || slideInterval) {
      return;
    }

    _clearInterval();

    slideInterval = setInterval(
      () => slide(SLIDE_ORDER_NEXT),
      slideIntervalDuration
    );
  }

  function handleKeyControl(event: KeyboardEvent) {
    const target = event.target as HTMLElement;
    const key = event.key;

    if (/input|textarea/i.test(target.tagName)) {
      return;
    }

    const direction =
      key === "ArrowLeft"
        ? SLIDE_ORDER_PREV
        : key === "ArrowRight"
        ? SLIDE_ORDER_NEXT
        : "";
    if (direction) {
      slide(direction);
      event.preventDefault();
    }
  }

  function handleSlideMouseOver(event: MouseEvent) {
    const target = event.target as HTMLElement;
    const closestParent = target.closest(
      ".slider-item-container .slider-image"
    );
    closestParent ? pause() : cycle();
  }

  function addEvents() {
    if (pauseOnHover) {
      sliderContentElement.addEventListener("mouseover", handleSlideMouseOver);
      sliderContentElement.addEventListener("mouseleave", cycle);
      document.addEventListener("keydown", handleKeyControl);

      // stop when hover bottom buttons
      bottomButtonElement.addEventListener("mouseover", pause);
      bottomButtonElement.addEventListener("mouseleave", cycle);
      arrowButtonsElement.addEventListener("mouseover", pause);
      arrowButtonsElement.addEventListener("mouseleave", cycle);

      if (touchSupported) {
        images = sliderContentElement.querySelectorAll(
          ".slider-item-container .slider-image"
        );
        images.forEach((image) => {
          image.addEventListener("touchstart", handleTouchStart);
        });
        bottomButtonElement.addEventListener("touchstart", handleTouchStart);
        arrowButtonsElement.addEventListener("touchstart", handleTouchStart);
        document.addEventListener("touchend", handleTouchEnd);
      }
    }
  }

  function removeEvents() {
    if (pauseOnHover) {
      sliderContentElement.removeEventListener(
        "mouseover",
        handleSlideMouseOver
      );
      sliderContentElement.removeEventListener("mouseleave", cycle);
      document.removeEventListener("keydown", handleKeyControl);

      bottomButtonElement.removeEventListener("mouseover", pause);
      bottomButtonElement.removeEventListener("mouseleave", cycle);
      arrowButtonsElement.removeEventListener("mouseover", pause);
      arrowButtonsElement.removeEventListener("mouseleave", cycle);

      if (touchSupported) {
        images.forEach((image) => {
          image.removeEventListener("touchstart", handleTouchStart);
        });
        bottomButtonElement.removeEventListener("touchstart", handleTouchStart);
        arrowButtonsElement.removeEventListener("touchstart", handleTouchStart);
        document.removeEventListener("touchend", handleTouchEnd);
      }
    }
  }

  function playInitAnimationEnd(item: string) {
    if (item === "icon") {
      playIconInitAnimation = false;
    } else if (item === "title") {
      playTitleInitAnimation = false;
    } else if (item === "description") {
      playDescriptionInitAnimation = false;
    } else if (item === "waitlist") {
      playWaitlistInitAnimation = false;
    } else {
      playImageInitAnimation = false;
    }
  }

  function handleBottomButtonClick(index: number) {
    slideTo(index);
  }

  function calculateArrowDistance() {
    const sliderBound = sliderContainerElement.getBoundingClientRect();
    const max_distance = 100;
    const distance = sliderBound.left / 2 + 24 - 18;

    arrowPosition = `${-Math.min(
      max_distance,
      Math.max(distance, sliderBound.left - 8)
    )}px`;
  }

  function handleTouchStart() {
    pause();
    if (touchEndTimeout) {
      clearTimeout(touchEndTimeout);
      touchEndTimeout = null;
    }
  }

  function handleTouchEnd(event: TouchEvent) {
    if (event.touches.length === 0 && !slideInterval) {
      if (touchEndTimeout) {
        clearTimeout(touchEndTimeout);
      }

      touchEndTimeout = setTimeout(() => {
        cycle();
        touchEndTimeout = null;
      }, TOUCHEVENT_COMPAT_WAIT);
    }
  }

  function controlSlidingCb(stop: boolean) {
    stopSliding = stop;
  }

  onMount(() => {
    cycle();
    addEvents();
    calculateArrowDistance();
    window.addEventListener("resize", calculateArrowDistance);

    return () => {
      removeEvents();
      window.removeEventListener("resize", calculateArrowDistance);
    };
  });

  $: hasItem = items.length > 0;
  $: placeholderItem = items[items.length - 1];
  $: sliderStyle = `--arrow-position: ${arrowPosition}; --init-animation-duration: ${initAnimationDuration}; --switch-slide-animation-duration: ${switchSlideAnimationDuration};`;
</script>

{#if hasItem}
  <div
    class="slider-container"
    class:mobile={isMobile}
    bind:this={sliderContainerElement}
    style={sliderStyle}
  >
    {#if placeholderItem}
      <Item item={placeholderItem} isPlaceholder={true} />
    {/if}

    <div class="slider-content-wrapper">
      <div class="slider-content" bind:this={sliderContentElement}>
        {#each items as item, index}
          {@const isActive = activeIndex === index}

          <Item
            {item}
            {isActive}
            {playInitAnimationEnd}
            {playIconInitAnimation}
            {playTitleInitAnimation}
            {playDescriptionInitAnimation}
            {playImageInitAnimation}
            {playWaitlistInitAnimation}
            {controlSlidingCb}
          />
        {/each}
      </div>
    </div>

    <div class="slider-bottom-control" bind:this={bottomButtonElement}>
      {#each items as item, index}
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <div
          class="slider-control-button"
          class:active={activeIndex === index}
          on:click={() => handleBottomButtonClick(index)}
        />
      {/each}
    </div>

    <div class="arrow-button-container" bind:this={arrowButtonsElement}>
      <button
        class="slider-arrow-button left-arrow-button"
        on:click={handleLeftArrowClick}
      >
        <Icon
          icon="splide-arrow-left"
          width={arrowIconWidth}
          height={arrowIconHeight}
          fill="none"
        />
      </button>

      <button
        class="slider-arrow-button right-arrow-button"
        on:click={handleRightArrowClick}
      >
        <Icon
          icon="splide-arrow-right"
          width={arrowIconWidth}
          height={arrowIconHeight}
          fill="none"
        />
      </button>
    </div>
  </div>
{/if}

<!-- prefetch large size image -->

<!-- {#each items as sliderItem}
  <div style="display: none;">
    <img src={sliderItem.image} alt="" />
  </div>
{/each} -->
<style lang="postcss">
  .slider-container {
    @apply relative mb-12;
    width: min(1200px, calc(100% - 48px));
    text-align: center;
  }

  .slider-container.mobile {
    width: calc(100% - 32px);
  }

  .arrow-button-container {
    @apply absolute h-12 flex flex-row items-center justify-between;
    top: calc(50% + 56px);
    left: var(--arrow-position);
    right: var(--arrow-position);
    z-index: 10;
  }

  .mobile .arrow-button-container {
    @apply items-end left-0 right-0 px-6;
    top: calc(100% - 8px);
    height: 58px;
  }

  .slider-arrow-button {
    @apply flex flex-row items-center justify-center w-12 h-12 rounded-full text-center;
    background: #f7f9fa;
    border: 0px;
    z-index: 10000;
    color: #a7b0b5;
    opacity: 0.7;
  }

  .mobile .slider-arrow-button {
    @apply w-9 h-9;
  }

  .slider-container:not(.mobile) .slider-arrow-button:hover,
  .slider-arrow-button:active {
    color: #0e0121;
    background: white;
    box-shadow: 5px 15px 30px rgba(55, 84, 170, 0.16);
    opacity: 1;
  }

  .slider-container:not(.mobile) .slider-arrow-button {
    --duration: 0.3s;
    transition: color var(--duration) ease-in-out,
      background var(--duration) ease-in-out,
      opacity var(--duration) ease-in-out,
      box-shadow var(--duration) ease-in-out;
  }

  .slider-content-wrapper {
    @apply absolute left-0 right-0 top-0 bottom-0;
  }

  .slider-content {
    @apply relative w-full h-full;
  }

  button:active {
    background-color: white;
  }

  .slider-bottom-control {
    @apply absolute left-0 right-0 flex flex-row items-end justify-between mx-auto;
    top: calc(100% - 8px);
    padding: 0px 4px 4px 4px;
    height: 48px;
    width: 50px;
    z-index: 1000;
  }

  .mobile .slider-bottom-control {
    pointer-events: none;
  }

  .slider-control-button {
    @apply relative rounded-full cursor-pointer;
    width: 8px;
    height: 8px;
    background-color: #5f89ff;
    opacity: 0.3;
  }

  .mobile .slider-control-button {
    pointer-events: auto;
  }

  .mobile .slider-control-button::before {
    @apply absolute -left-0.5 -right-0.5 -top-0.5 -bottom-0.5 bg-transparent;
    content: "";
  }

  .slider-control-button.active {
    opacity: 1;
  }

  .slider-control-button:global(.current) {
    transition: opacity var(--switch-slide-animation-duration) ease-in-out;
    opacity: 0.3;
  }

  .slider-control-button:global(.next) {
    transition: opacity var(--switch-slide-animation-duration) ease-in-out;
    opacity: 1;
  }
</style>
